"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import type { ApiWaveVotersPage } from "@/generated/models/ApiWaveVotersPage";
import type { ApiWaveVoter } from "@/generated/models/ApiWaveVoter";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
interface UseWaveTopVotersProps {
  readonly waveId: string;
  readonly connectedProfileHandle: string | undefined;
  readonly reverse: boolean;
  readonly dropId: string | null;
  readonly sortDirection?: "ASC" | "DESC" | undefined;
  readonly sort?: "ABSOLUTE" | "POSITIVE" | "NEGATIVE" | undefined;
  readonly refetchInterval?: number | undefined;
  readonly enabled?: boolean | undefined;
}
export function useWaveTopVoters(t0) {
  const $ = _c(37);
  const {
    waveId,
    connectedProfileHandle,
    reverse,
    dropId,
    sortDirection: t1,
    sort: t2,
    refetchInterval: t3,
    enabled: t4
  } = t0;
  const sortDirection = t1 === undefined ? "ASC" : t1;
  const sort = t2 === undefined ? "ABSOLUTE" : t2;
  const refetchInterval = t3 === undefined ? Infinity : t3;
  const enabled = t4 === undefined ? true : t4;
  const queryClient = useQueryClient();
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const [voters, setVoters] = useState(t5);
  const canFetch = enabled && !!connectedProfileHandle;
  let t6;
  if ($[1] !== dropId || $[2] !== sort || $[3] !== sortDirection || $[4] !== waveId) {
    t6 = [QueryKey.WAVE_VOTERS, {
      waveId,
      dropId,
      sortDirection,
      sort
    }];
    $[1] = dropId;
    $[2] = sort;
    $[3] = sortDirection;
    $[4] = waveId;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const queryKey = t6;
  let t7;
  let t8;
  if ($[6] !== canFetch || $[7] !== dropId || $[8] !== queryClient || $[9] !== queryKey || $[10] !== sort || $[11] !== sortDirection || $[12] !== waveId) {
    t7 = () => {
      if (!canFetch) {
        return;
      }
      queryClient.prefetchInfiniteQuery({
        queryKey,
        queryFn: async t9 => {
          const {
            pageParam
          } = t9;
          const params = {
            page_size: "20",
            sort_direction: sortDirection,
            sort
          };
          if (dropId) {
            params.drop_id = dropId;
          }
          if (pageParam !== null) {
            params.page = pageParam.toString();
          }
          return await commonApiFetch({
            endpoint: `waves/${waveId}/voters`,
            params
          });
        },
        initialPageParam: null,
        getNextPageParam: _temp,
        pages: 3,
        staleTime: 60000,
        ...getDefaultQueryRetry()
      });
    };
    t8 = [canFetch, queryKey, queryClient, waveId, dropId, sortDirection, sort];
    $[6] = canFetch;
    $[7] = dropId;
    $[8] = queryClient;
    $[9] = queryKey;
    $[10] = sort;
    $[11] = sortDirection;
    $[12] = waveId;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  if ($[15] !== dropId || $[16] !== sort || $[17] !== sortDirection || $[18] !== waveId) {
    t9 = async t10 => {
      const {
        pageParam: pageParam_0
      } = t10;
      const params_0 = {
        page_size: "20",
        sort_direction: sortDirection,
        sort
      };
      if (dropId) {
        params_0.drop_id = dropId;
      }
      if (pageParam_0 !== null) {
        params_0.page = pageParam_0.toString();
      }
      return await commonApiFetch({
        endpoint: `waves/${waveId}/voters`,
        params: params_0
      });
    };
    $[15] = dropId;
    $[16] = sort;
    $[17] = sortDirection;
    $[18] = waveId;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = getDefaultQueryRetry();
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== canFetch || $[22] !== queryKey || $[23] !== refetchInterval || $[24] !== t9) {
    t11 = {
      queryKey,
      queryFn: t9,
      initialPageParam: null,
      getNextPageParam: _temp2,
      placeholderData: keepPreviousData,
      enabled: canFetch,
      staleTime: 60000,
      refetchInterval,
      ...t10
    };
    $[21] = canFetch;
    $[22] = queryKey;
    $[23] = refetchInterval;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    refetch
  } = useInfiniteQuery(t11);
  let t12;
  let t13;
  if ($[26] !== data || $[27] !== reverse) {
    t12 = () => {
      setVoters(() => {
        const newVoters = (data?.pages ? data.pages : []).flatMap(_temp3);
        return reverse ? [...newVoters].reverse() : newVoters;
      });
    };
    t13 = [data, reverse];
    $[26] = data;
    $[27] = reverse;
    $[28] = t12;
    $[29] = t13;
  } else {
    t12 = $[28];
    t13 = $[29];
  }
  useEffect(t12, t13);
  let t14;
  if ($[30] !== fetchNextPage || $[31] !== hasNextPage || $[32] !== isFetchingNextPage || $[33] !== isLoading || $[34] !== refetch || $[35] !== voters) {
    t14 = {
      voters,
      fetchNextPage,
      hasNextPage,
      isLoading,
      isFetchingNextPage,
      refetch
    };
    $[30] = fetchNextPage;
    $[31] = hasNextPage;
    $[32] = isFetchingNextPage;
    $[33] = isLoading;
    $[34] = refetch;
    $[35] = voters;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
}
function _temp3(page) {
  return page.data;
}
function _temp2(lastPage_0, allPages_0) {
  if (!lastPage_0.next) {
    return null;
  }
  const currentPage_0 = typeof lastPage_0.page === "number" ? lastPage_0.page : allPages_0.length - 1;
  return currentPage_0 + 1;
}
function _temp(lastPage, allPages) {
  if (!lastPage.next) {
    return null;
  }
  const currentPage = typeof lastPage.page === "number" ? lastPage.page : allPages.length - 1;
  return currentPage + 1;
}